import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Film, ImageIcon, Upload, X, Plus } from 'lucide-react';
import {
  ComposerItem,
  LinkSource,
  MAX_MEDIA_ITEMS,
  addFiles,
  addLink,
  extractYouTubeId,
  moveItem,
  releaseItems,
  removeItem,
} from './mediaUtils';

function Thumb({ item }: { item: ComposerItem }) {
  if (item.kind === 'existing') {
    const m = item.item;
    const id = m.source === 'youtube' ? extractYouTubeId(m.videoUrl) : null;
    if (id) return <img src={`https://img.youtube.com/vi/${id}/default.jpg`} alt="" className="h-full w-full object-cover" />;
    return (
      <div className="flex h-full w-full items-center justify-center bg-gray-100">
        {m.kind === 'image' ? <ImageIcon className="h-6 w-6 text-gray-400" /> : <Film className="h-6 w-6 text-[#3b82f6]" />}
      </div>
    );
  }
  if (item.kind === 'image' && item.previewUrl) {
    return <img src={item.previewUrl} alt="" className="h-full w-full object-cover" />;
  }
  if (item.kind === 'video' && item.source === 'youtube') {
    const id = extractYouTubeId(item.url);
    if (id) return <img src={`https://img.youtube.com/vi/${id}/default.jpg`} alt="" className="h-full w-full object-cover" />;
  }
  return (
    <div className="flex h-full w-full items-center justify-center bg-gray-100">
      <Film className="h-6 w-6 text-[#3b82f6]" />
    </div>
  );
}

function itemLabel(item: ComposerItem): string {
  if (item.kind === 'existing') {
    const m = item.item;
    if (m.kind === 'image') return 'Saved image';
    return m.source === 'youtube' ? 'Saved YouTube link' : m.source === 'vidcast' ? 'Saved Vidcast link' : 'Saved video';
  }
  if (item.kind === 'image') return item.file.name;
  if (item.source === 'upload') return item.file.name;
  return item.source === 'youtube' ? 'YouTube link' : 'Vidcast link';
}

export default function MediaComposer({ items, onChange, disabled = false }: {
  items: ComposerItem[]
  onChange: (items: ComposerItem[]) => void
  disabled?: boolean
}) {
  const [linkSource, setLinkSource] = useState<LinkSource>('youtube');
  const [linkUrl, setLinkUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const full = items.length >= MAX_MEDIA_ITEMS;

  const handleFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const res = addFiles(items, Array.from(list));
    setError(res.error);
    onChange(res.items);
  };

  const handleAddLink = () => {
    const res = addLink(items, linkSource, linkUrl);
    setError(res.error);
    if (!res.error) {
      setLinkUrl('');
      onChange(res.items);
    }
  };

  const handleRemove = (item: ComposerItem) => {
    releaseItems([item]);
    setError(null);
    onChange(removeItem(items, item.id));
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-100/60 p-3.5">
      <div className="mb-2 flex items-center justify-between">
        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Photos and videos</label>
        <span className="text-[11px] text-gray-500" data-testid="media-count">{items.length}/{MAX_MEDIA_ITEMS}</span>
      </div>

      {items.length > 0 && (
        <ul className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {items.map((item, i) => (
            <li key={item.id} className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="relative h-24">
                <Thumb item={item} />
                <span className="absolute left-1.5 top-1.5 rounded bg-gray-900/70 px-1.5 text-[10px] font-semibold text-white">{i + 1}</span>
                <button
                  type="button"
                  aria-label={`Remove item ${i + 1}`}
                  disabled={disabled}
                  onClick={() => handleRemove(item)}
                  className="absolute right-1.5 top-1.5 rounded bg-gray-900/70 p-1 text-white transition hover:bg-gray-900 disabled:opacity-40"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
              <div className="flex items-center gap-1 px-1.5 py-1">
                <p className="min-w-0 flex-1 truncate text-[11px] text-gray-600">{itemLabel(item)}</p>
                <button
                  type="button"
                  aria-label={`Move item ${i + 1} left`}
                  disabled={disabled || i === 0}
                  onClick={() => onChange(moveItem(items, item.id, -1))}
                  className="rounded p-0.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  aria-label={`Move item ${i + 1} right`}
                  disabled={disabled || i === items.length - 1}
                  onClick={() => onChange(moveItem(items, item.id, 1))}
                  className="rounded p-0.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                >
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <label className={`flex h-20 w-full flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-100 transition ${full || disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-[#60a5fa] hover:bg-[#eff6ff]/30'}`}>
        <Upload className="mb-1 h-5 w-5 text-gray-500" />
        <p className="text-sm font-semibold text-gray-700">Add photos or videos</p>
        <p className="text-xs text-gray-500">Images up to 10 MB, any mix, max {MAX_MEDIA_ITEMS} items</p>
        <input
          type="file"
          multiple
          accept="image/png,image/jpeg,image/gif,image/webp,video/*"
          aria-label="Add photos or videos"
          disabled={full || disabled}
          className="hidden"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
        />
      </label>

      <div className="mt-3 flex gap-2">
        <select
          value={linkSource}
          onChange={(e) => setLinkSource(e.target.value as LinkSource)}
          aria-label="Link type"
          className="rounded-lg border border-gray-200 bg-white px-2 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#60a5fa]"
        >
          <option value="youtube">YouTube</option>
          <option value="vidcast">Vidcast</option>
        </select>
        <input
          type="url"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddLink(); } }}
          placeholder={linkSource === 'youtube' ? 'https://www.youtube.com/watch?v=...' : 'https://app.vidcast.io/share/embed/...'}
          aria-label="Video link"
          className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#60a5fa]"
        />
        <button
          type="button"
          onClick={handleAddLink}
          disabled={!linkUrl.trim() || full || disabled}
          className="flex items-center gap-1 rounded-lg bg-[#3b82f6] px-3 py-2 text-sm font-semibold text-white transition hover:bg-[#2563eb] disabled:opacity-40"
        >
          <Plus className="h-4 w-4" /> Add link
        </button>
      </div>

      {error && <p role="alert" className="mt-2 text-xs font-medium text-[#dc2626]">{error}</p>}
      {full && !error && <p className="mt-2 text-xs text-[#b45309]">Maximum of {MAX_MEDIA_ITEMS} items reached.</p>}
    </div>
  );
}
